import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export enum IncidentType {
  CollisionWithVehicle = 'Collision with Vehicle',
  CollisionWithObject = 'Collision with Object',
  Vandalism = 'Vandalism',
  WeatherRelated = 'Weather Related',
  Theft = 'Theft',
  Other = 'Other'
}

interface IncidentDetailsState {
  incidentId: number;
  incidentDate: Date;
  policeCaseNumber: string;
  accidentLocationDescription: string;
  additionalSafetyNotes: string;
  injuryComplaints: boolean;
  incidentType: IncidentType | '';
  weatherConditions: string;
  roadConditions: string;
  witnessName: string;
  witnessPhone: string;
  witnessStatement: string;
  isFinalized: boolean;
}

const initialState: IncidentDetailsState = {
  incidentId: 0,
  incidentDate: new Date(),
  policeCaseNumber: '',
  accidentLocationDescription: '',
  additionalSafetyNotes: '',
  injuryComplaints: false,
  incidentType: '',
  weatherConditions: '',
  roadConditions: '',
  witnessName: '',
  witnessPhone: '',
  witnessStatement: '',
  isFinalized: false
};

const incidentDetailsSlice = createSlice({
  name: 'incidentDetails',
  initialState,
  reducers: {
    setIncidentId: (state, action: PayloadAction<number>) => {
      state.incidentId = action.payload;
    },
    setIncidentDate: (state, action: PayloadAction<Date | string>) => {
      state.incidentDate = typeof action.payload === 'string' 
        ? new Date(action.payload) 
        : action.payload;
    },
    setPoliceCaseNumber: (state, action: PayloadAction<string>) => {
      state.policeCaseNumber = action.payload;
    },
    setAccidentLocation: (state, action: PayloadAction<string>) => {
      state.accidentLocationDescription = action.payload;
    },
    setAdditionalSafetyNotes: (state, action: PayloadAction<string>) => {
      state.additionalSafetyNotes = action.payload;
    },
    setInjuryComplaints: (state, action: PayloadAction<boolean>) => {
      state.injuryComplaints = action.payload;
    },
    setIncidentType: (state, action: PayloadAction<IncidentType | ''>) => {
      state.incidentType = action.payload;
    },
    setWeatherConditions: (state, action: PayloadAction<string>) => {
      state.weatherConditions = action.payload;
    },
    setRoadConditions: (state, action: PayloadAction<string>) => {
      state.roadConditions = action.payload;
    },
    setWitnessInfo: (
      state,
      action: PayloadAction<{
        name?: string;
        phone?: string;
        statement?: string;
      }>
    ) => {
      if (action.payload.name !== undefined) {
        state.witnessName = action.payload.name;
      }
      if (action.payload.phone !== undefined) {
        state.witnessPhone = action.payload.phone;
      }
      if (action.payload.statement !== undefined) {
        state.witnessStatement = action.payload.statement;
      }
    },
    setIsFinalized: (state, action: PayloadAction<boolean>) => {
      state.isFinalized = action.payload;
    },
    clearIncidentDetails: () => initialState,
    setAllIncidentDetails: (state, action: PayloadAction<Partial<IncidentDetailsState>>) => {
      return { ...state, ...action.payload };
    }
  }
});

export const {
  setIncidentId,
  setIncidentDate,
  setPoliceCaseNumber,
  setAccidentLocation,
  setAdditionalSafetyNotes,
  setInjuryComplaints,
  setIncidentType,
  setWeatherConditions,
  setRoadConditions,
  setWitnessInfo,
  setIsFinalized,
  clearIncidentDetails,
  setAllIncidentDetails
} = incidentDetailsSlice.actions;

export default incidentDetailsSlice.reducer;

// Helper functions
export const getIncidentTypeOptions = (): string[] => {
  return Object.values(IncidentType);
};

export const isIncidentDetailsComplete = (state: IncidentDetailsState): boolean => {
  // Required fields for operator role
  return (
    state.incidentType !== '' &&
    state.accidentLocationDescription.trim() !== ''
  );
};